/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { Outlet, type RouteObject } from 'react-router-dom'

import RoutablePage from '@modules/routing/RoutablePage'

import RedirectToDeploymentResource from '@/components/RedirectToDeploymentResource'
import HealthStatus from '@/components/Deployment/HealthStatus'
import HealthStatusDetails from '@/components/Deployment/HealthStatusDetails'

import APIConsole from '../../components/APIConsole'
import ClusterSnapshots from '../../components/Cluster/Snapshots'
import SnapshotDetails from '../../components/Cluster/Snapshots/SnapshotDetails'
import Deployment from '../../components/Deployment'
import DeploymentOverview from '../../components/Deployment/Overview'
import NotFound from '../../components/ErrorPages/NotFound'
import SsoErrors from '../../components/ErrorPages/SsoErrors'
import EditStackDeployment from '../../components/StackDeploymentEditor/EditStackDeployment'
import StackDeploymentActivity from '../../components/StackDeploymentActivity'
import StackDeploymentSlider from '../../components/StackDeploymentSlider'
import Logout from '../../components/Logout'
import DeploymentExtensions from '../../components/DeploymentExtensions'
import DeploymentExtensionsGate from '../../components/DeploymentExtensionsGate'
import DeploymentExtensionCreate from '../../components/DeploymentExtensionCreate'
import DeploymentExtensionEdit from '../../components/DeploymentExtensionEdit'

import AppRoot from './components/AppRoot'
import AuthenticatedPageWrapper from './components/AuthenticatedPageWrapper'
import Tracking from './components/Tracking'
import ClusterOverview from './components/Cluster/Overview'
import Support from './components/Support'
import LogsMetrics from './components/Deployment/Logs'
import PerformanceMetrics from './components/PerformanceMetrics'
import DeploymentSecurity from './components/DeploymentSecurity'

export function getHerokuRoutes(): RouteObject[] {
  return [
    {
      element: <AppRoot />,
      children: [
        ...getUntrackedRoutes(),
        {
          element: <Tracking />,
          children: getLoggedInRoutes(),
        },
        {
          path: '*',
          element: (
            <RoutablePage title='Not found'>
              <NotFound />
            </RoutablePage>
          ),
        },
      ],
    },
  ]

  function getUntrackedRoutes(): RouteObject[] {
    return [
      {
        path: 'logout',
        element: (
          <RoutablePage title='Logging out'>
            <Logout />
          </RoutablePage>
        ),
      },
      {
        path: 'errors/sso',
        element: (
          <RoutablePage title='Error'>
            <SsoErrors />
          </RoutablePage>
        ),
      },
    ]
  }

  function getLoggedInRoutes(): RouteObject[] {
    return [
      {
        element: <AuthenticatedPageWrapper />,
        children: [
          ...getStackDeploymentRoutes(),
          ...getExtensionsRoutes(),

          {
            path: 'support',
            element: (
              <RoutablePage title='Support'>
                <Support />
              </RoutablePage>
            ),
          },
        ],
      },
    ]
  }

  function getStackDeploymentRoutes(): RouteObject[] {
    return [
      {
        path: 'deployments/resolve/:resourceType/:regionId/:resourceId',
        element: <RedirectToDeploymentResource />,
      },
      {
        path: 'deployments/resolve/:resourceType/:regionId/:resourceId/*',
        element: <RedirectToDeploymentResource />,
      },
      {
        path: 'deployments/:deploymentId',
        element: <Deployment />,
        children: [
          {
            path: '/deployments/:deploymentId',
            element: (
              <RoutablePage title='Overview'>
                <DeploymentOverview />
              </RoutablePage>
            ),
          },
          {
            path: '/deployments/:deploymentId/edit',
            element: (
              <RoutablePage title='Edit deployment'>
                <EditStackDeployment />
              </RoutablePage>
            ),
          },
          {
            path: '/deployments/:deploymentId/elasticsearch',
            element: (
              <RoutablePage title='Manage Elasticsearch'>
                <ClusterOverview />
              </RoutablePage>
            ),
          },
          {
            path: '/deployments/:deploymentId/elasticsearch/console',
            element: (
              <RoutablePage title='Deployment console'>
                <APIConsole />
              </RoutablePage>
            ),
          },
          {
            path: '/deployments/:deploymentId/elasticsearch/snapshots',
            element: (
              <RoutablePage title='Deployment snapshots'>
                <ClusterSnapshots />
              </RoutablePage>
            ),
          },
          {
            path: '/deployments/:deploymentId/elasticsearch/snapshots/:snapshotName',
            element: (
              <RoutablePage title='Snapshot'>
                <SnapshotDetails />
              </RoutablePage>
            ),
          },
          {
            path: '/deployments/:deploymentId/kibana',
            element: (
              <RoutablePage title='Manage Kibana'>
                <StackDeploymentSlider />
              </RoutablePage>
            ),
          },
          {
            path: '/deployments/:deploymentId/activity',
            element: (
              <RoutablePage title='Deployment Activity'>
                <StackDeploymentActivity />
              </RoutablePage>
            ),
          },
          {
            path: '/deployments/:deploymentId/activity/elasticsearch',
            element: (
              <RoutablePage title='Elasticsearch Activity'>
                <StackDeploymentActivity />
              </RoutablePage>
            ),
          },
          {
            path: '/deployments/:deploymentId/activity/kibana',
            element: (
              <RoutablePage title='Kibana Activity'>
                <StackDeploymentActivity />
              </RoutablePage>
            ),
          },
          {
            path: '/deployments/:deploymentId/logs-metrics',
            element: (
              <RoutablePage title='Logs and metrics'>
                <LogsMetrics />
              </RoutablePage>
            ),
          },
          {
            path: '/deployments/:deploymentId/health',
            element: (
              <RoutablePage title='Health'>
                <HealthStatus />
              </RoutablePage>
            ),
          },
          {
            path: '/deployments/:deploymentId/health/:issue',
            element: (
              <RoutablePage title='Health status'>
                <HealthStatusDetails />
              </RoutablePage>
            ),
          },
          {
            path: '/deployments/:deploymentId/metrics',
            element: (
              <RoutablePage title='Performance'>
                <PerformanceMetrics />
              </RoutablePage>
            ),
          },
          {
            path: '/deployments/:deploymentId/security',
            element: (
              <RoutablePage title='Security'>
                <DeploymentSecurity />
              </RoutablePage>
            ),
          },
        ],
      },
    ]
  }

  function getExtensionsRoutes(): RouteObject[] {
    return [
      {
        path: 'extensions',
        element: (
          <DeploymentExtensionsGate>
            <Outlet />
          </DeploymentExtensionsGate>
        ),
        children: [
          {
            index: true,
            element: (
              <RoutablePage title='Deployment extensions'>
                <DeploymentExtensions />
              </RoutablePage>
            ),
          },
          {
            path: 'create',
            element: (
              <RoutablePage title='Create deployment extension'>
                <DeploymentExtensionCreate />
              </RoutablePage>
            ),
          },
          {
            path: ':extensionId/edit',
            element: (
              <RoutablePage title='Deployment extension'>
                <DeploymentExtensionEdit />
              </RoutablePage>
            ),
          },
        ],
      },
    ]
  }
}
